import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Region } from '../../../models/region.model';
import { NetworkLines, NetworkType } from '../../../enums/network-type.enum';
import { AdvancedMapFilter } from '../../../models/advancedMap.model';
import { Area } from '../../../models/area.model';
import { DtForSelect } from '../../../models/dt.model';
import { SelectOptionAdvMapDto, SelectOptionDto } from '../../../models/keyValueItem.model';
import { PoleForSelect } from '../../../models/pole-map.model';
import { NetworkAssemblyFilterOptions } from '../../../enums/network-assembly-filter-options.enum';
import { RegionService } from '../../../services/region.service';
import { AreaService } from '../../../services/area.service';
import { DtService } from '../../../services/dt.service';
import { PoleService } from '../../../services/pole.service';
import { AuthService } from 'src/app/services/auth.service';
import { CimSubstationService, GeographicalRegion, GeographicalRegionService, SubGeographicalRegion, SubGeographicalRegionService, SubstationTemplateDto, SubstationTemplateService, UtilitySharedService } from 'shared-lib';

type FilterOptionMap = {
  [NetworkType.Structure]: { Type: string[]; Region: Region[] };
  [NetworkType.Lines]: { Type: string[] };
  [NetworkType.Device]: { Type: string[] };
};

@Component({
  selector: 'app-advanced-map-filter',
  templateUrl: './advanced-map-filter.component.html',
  styleUrls: ['./advanced-map-filter.component.scss'],
  standalone: false

})
export class AdvancedMapFilterComponent implements OnInit {
  @Input() filter: AdvancedMapFilter;
  @Input() appliedFilters: { id: number, label: string, value: string }[];
  @Output() applyFilterClicked = new EventEmitter<boolean>();
  @Output() closeFilterClicked = new EventEmitter<boolean>();

  regions: Region[] = [];
  areas: Area[] = [];
  dts: DtForSelect[] = [];
  dtsForSelect: SelectOptionAdvMapDto[] = [];

  selectedPoles: number[] = [];

  NetworkType = NetworkType;
  topFilters = ['Network Structure', 'Network Lines', 'Equipment'];
  selectedNetwork: NetworkType = NetworkType.Structure;

  selectedFilterType: string = 'Type';

  filteredPoles: PoleForSelect[] = [];

  AssemblyFilterOptions = NetworkAssemblyFilterOptions;
  substationTemplates: SubstationTemplateDto[] = [];
  cimSubstations: any[] = [];
  cimSubstationsForSelect: SelectOptionDto[] = [];

  geographicalRegions: GeographicalRegion[] = [];
  subGeographicalRegions: SubGeographicalRegion[] = [];

  selectedOptionType: string | null = null;
  selectedOptionRegion: Region | null = null;
  selectedOptionGeographicalRegion: GeographicalRegion | null = null;

  dtSearchText: string = '';
  poleSearchText: string = '';
  areaSearchText: string = '';
  regionSearchText: string = '';
  cimSubstationSearchText: string = '';
  utilityId: number = 0;
  selectedSubstationTemplate: SubstationTemplateDto | null = null;
  hasCimDataIntegration: boolean = false;

  shouldPullPoles: boolean = true;
  constructor(private regionService: RegionService,
    private areaService: AreaService,
    private dtService: DtService,
    private poleService: PoleService,
    private substationTemplateService: SubstationTemplateService,
    private cimSubstationService: CimSubstationService,
    private geographicalRegionService: GeographicalRegionService,
    private subGeographicalRegionService: SubGeographicalRegionService,
    private utilitySharedService: UtilitySharedService,
    private authService: AuthService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;
  }

  ngOnInit(): void {
    if (this.hasCimDataIntegration) {
      this.getGeographicalRegions();
      this.getSubGeographicalRegions();
      this.getSubstationTemplates();
    } else {
      this.getRegions();
      this.getAreas();
      this.getDts();
    }
  }

  getSubstationTemplates() {
    const latestUtilityId = Number(this.utilitySharedService.getUtilityId());
    if (!latestUtilityId) {
      this.substationTemplates = [];
      return;
    }

    this.utilityId = latestUtilityId;

    this.substationTemplateService.getAll(this.utilityId).subscribe({
      next: (response: any) => {
        const templates = response?.body?.data;
        this.substationTemplates = Array.isArray(templates) ? templates : [];
      },
      error: (error: any) => {
        this.toastr.error('Error occurred while fetching substation templates');
      }
    });
  }

  selectNetwork(item: string) {
    if (item === 'Network Structure') {
      item = NetworkType.Structure;
    }
    else if (item === 'Network Lines') {
      item = NetworkType.Lines;
    }
    else if (item === 'Equipment') {
      item = NetworkType.Device;
    }
    this.selectedNetwork = item as NetworkType;
    this.selectedOptionType = null;
    this.selectedOptionRegion = null;
    this.selectedFilterType = 'Type';
  }

  selectFilterType(type: string) {
    this.selectedFilterType = type;
    this.selectedOptionType = null;
    this.selectedOptionRegion = null;
  }

  selectFilterOptionType(option: string) {
    this.selectedOptionType = option;

    switch (this.selectedOptionType) {
      case 'LV':
        // if(this.shouldPullPoles && this.filter.selectedDts.length > 0) {
        //   this.filter.selectedPoles = [];
        //   this.getPolesFilter();
        //   this.shouldPullPoles = false;
        // }
        break;
      default:
        break;
    }
  }

  selectSubstationTemplateFilter(ss: SubstationTemplateDto) {
    this.selectedSubstationTemplate = ss;

    this.cimSubstationService.getAllForSelectMap({ templateId: ss.id!, searchTerm: this.cimSubstationSearchText }).subscribe({
      next: (response: any) => {
        if (response?.body?.status === 200 || response?.body?.status === 'OK') {
          this.cimSubstations = response?.body?.data || [];
          this.setCimSubstationsForSelect();
        }
      }
    });
  }

  // only substations of the selected sub geographical regions (all when none is selected)
  private setCimSubstationsForSelect() {
    const selectedSubRegions = this.filter.selectedSubGeographicalRegions;
    this.cimSubstationsForSelect = this.cimSubstations
      .filter(x => selectedSubRegions.length == 0 || selectedSubRegions.includes(x.subGeographicalRegionId))
      .map(x => {
        return {
          id: x.id,
          name: x.name,
          checked: false
        };
      });
  }

  selectFilterOptionRegion(option: Region) {
    this.selectedOptionRegion = option;
  }

  selectFilterOptionGeographicalRegion(option: GeographicalRegion) {
    this.selectedOptionGeographicalRegion = option;
  }

  get filterOptionsByRegion(): Region[] {
    if (this.regionSearchText?.trim()) {
      const search = this.regionSearchText.toLowerCase();
      let filteredRegions = this.regions.filter(region => region.name.toLowerCase().includes(search));
      return filteredRegions;
    }
    return this.regions;
  }

  get filterOptionsByGeographicalRegion(): GeographicalRegion[] {
    if (this.regionSearchText?.trim()) {
      const search = this.regionSearchText.toLowerCase();
      let filteredGeographicalRegions = this.geographicalRegions.filter(region => region.name.toLowerCase().includes(search));
      return filteredGeographicalRegions;
    }
    return this.geographicalRegions;
  }

  get filterOptionsByType(): string[] {
    switch (this.selectedNetwork) {
      case NetworkType.Structure:
        return Object.values(NetworkAssemblyFilterOptions);
      case NetworkType.Lines:
        return Object.values(NetworkLines);
      case NetworkType.Device:
        return [];
      default:
        return [];
    }
  }

  get filteredCimSubstations(): SelectOptionDto[] {
    if (!this.cimSubstationSearchText) {
      return this.cimSubstationsForSelect;
    }

    const search = this.cimSubstationSearchText.toLowerCase();
    return this.cimSubstationsForSelect.filter(ss => ss.name.toLowerCase().includes(search));
  }

  get filteredDts(): SelectOptionDto[] {
    if (!this.dtSearchText) {
      return this.dtsForSelect;
    }

    const search = this.dtSearchText.toLowerCase();
    return this.dtsForSelect.filter(dt => dt.name.toLowerCase().includes(search));
  }

  get filteredPolesBySlrn(): PoleForSelect[] {
    if (!this.poleSearchText) {
      return this.filteredPoles;
    }

    const search = this.poleSearchText.toLowerCase();
    return this.filteredPoles.filter(x => x.slrn.toLowerCase().includes(search));
  }

  private getRegions() {
    this.regionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  private getAreas() {
    this.areaService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.areas = responseData.data;
        }
        else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getGeographicalRegions() {
    this.geographicalRegionService.getAllForSelect().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.geographicalRegions = responseData.data;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  private getSubGeographicalRegions() {
    this.subGeographicalRegionService.getAllForMappings().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.subGeographicalRegions = responseData.data;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  private getDts() {
    this.dtService.getAllForSelect().subscribe(
      (data) => {
        this.dts = data.body.data;
        this.dtsForSelect = this.dts.map(x => ({
          id: x.externalId,
          name: x.name,
          checked: false,
          areaId: x.areaId
        }));
      },
      (error) => {
        this.toastr.error('Error occured');
      }
    );
  }

  get filteredAreas(): Area[] {
    if (this.selectedFilterType === 'COMMON.BU.SINGLE' && this.selectedOptionRegion && 'id' in this.selectedOptionRegion) {
      const regionId = this.selectedOptionRegion.id;
      let filtered = this.areas.filter(area => area.regionId === regionId);

      if (this.areaSearchText) {
        const search = this.areaSearchText.toLowerCase();
        filtered = filtered.filter(area => area.name.toLowerCase().includes(search));
      }
      return filtered;
    }

    return [];
  }

  get filteredSubGeographicalRegions(): SubGeographicalRegion[] {
    if (this.selectedFilterType === 'COMMON.BU.SINGLE' && this.selectedOptionGeographicalRegion) {
      const geographicalRegionId = this.selectedOptionGeographicalRegion.id;
      let filtered = this.subGeographicalRegions.filter(sub => sub.geographicalRegion.id === geographicalRegionId);

      if (this.areaSearchText) {
        const search = this.areaSearchText.toLowerCase();
        filtered = filtered.filter(sub => sub.name.toLowerCase().includes(search));
      }
      return filtered;
    }

    return [];
  }

  getSelectedArea(area: Area) {
    this.shouldPullPoles = true;
    if (!this.filter.selectedAreas.includes(area.id)) {
      this.filter.selectedAreas.push(area.id);
      this.appliedFilters.push({ id: area.id, label: 'Area', value: area.name });
    } else {
      var index = this.filter.selectedAreas.indexOf(area.id);
      this.filter.selectedAreas.splice(index, 1);
      index = this.appliedFilters.findIndex(f =>
        f.id === area.id && f.label === 'Area' && f.value === area.name
      );
      this.appliedFilters.splice(index, 1);
    }
    const selectedAreas = this.filter.selectedAreas;
    if (selectedAreas.length > 0) {
      const filteredDts = this.dts.filter(dt => selectedAreas.includes(dt.areaId));
      this.dtsForSelect = filteredDts.map(x => ({
        id: x.externalId,
        name: x.name,
        areaId: x.areaId,
        checked: false
      }));
    } else {
      this.dtsForSelect = this.dts.map(x => ({
        id: x.externalId,
        name: x.name,
        areaId: x.areaId,
        checked: false
      }));
    }
  }

  getSelectedDt(item: SelectOptionDto) {
    this.shouldPullPoles = false;
    if (!this.filter.selectedDts.includes(item.id)) {
      this.filter.selectedDts.push(item.id);
      this.appliedFilters.push({ id: item.id, label: "Dt", value: item.name });
    } else {
      var index = this.filter.selectedDts.indexOf(item.id);
      this.filter.selectedDts.splice(index, 1);
      index = this.appliedFilters.findIndex(f =>
        f.id === item.id && f.label === 'Dt' && f.value === item.name
      );
      this.appliedFilters.splice(index, 1);
    }
  }

  getSelectedCimSubstation(item: SelectOptionDto) {
    this.shouldPullPoles = false;
    if (!this.filter.selectedCimSubstations.includes(item.id)) {
      this.filter.selectedCimSubstations.push(item.id);
    } else {
      var index = this.filter.selectedCimSubstations.indexOf(item.id);
      this.filter.selectedCimSubstations.splice(index, 1);
    }
  }

  getSelectedSubGeographicalRegion(subRegion: SubGeographicalRegion) {
    this.shouldPullPoles = true;
    if (!this.filter.selectedSubGeographicalRegions.includes(subRegion.id!)) {
      this.filter.selectedSubGeographicalRegions.push(subRegion.id!);
      this.appliedFilters.push({ id: subRegion.id!, label: 'Sub Geographical Region', value: subRegion.name });
    } else {
      var index = this.filter.selectedSubGeographicalRegions.indexOf(subRegion.id!);
      this.filter.selectedSubGeographicalRegions.splice(index, 1);
      index = this.appliedFilters.findIndex(f =>
        f.id === subRegion.id && f.label === 'Sub Geographical Region' && f.value === subRegion.name
      );
      this.appliedFilters.splice(index, 1);
    }
    this.setCimSubstationsForSelect();
  }

  getSelectedPole(status: Boolean, id: number) {
    if (status) {
      this.filter.selectedPoles.push(id);
    } else {
      var index = this.filter.selectedPoles.indexOf(id);
      this.filter.selectedPoles.splice(index, 1);
    }
  }

  getPolesFilter() {
    var obj = {
      selectedAreas: this.filter.selectedAreas,
      selectedDts: this.filter.selectedDts
    };
    this.poleService.getPolesForMapFilter(obj).subscribe(
      (data: any) => {
        this.filteredPoles = data.body.data;
      },
      (error: any) => {
        this.toastr.error('Error occured');
      }
    );
  }

  getAreaCountText(regionId: number): string {
    const count = this.areas.filter(area => area.regionId === regionId).length;
    return `${count} ${count === 1 ? 'District' : 'Districts'}`;
  }

  getSubGeographicalRegionCountText(geographicalRegionId: number | null): string {
    const count = this.subGeographicalRegions.filter(sub => sub.geographicalRegion.id === geographicalRegionId).length;
    return `${count} ${count === 1 ? 'District' : 'Districts'}`;
  }

  applyFilter() {
    this.applyFilterClicked.emit(true);
  }

  closeFilter() {
    this.closeFilterClicked.emit(true);
  }

  goBackToRegions(): void {
    this.selectedOptionRegion = null;
    this.areaSearchText = '';
  }

  goBackToGeographicalRegions(): void {
    this.selectedOptionGeographicalRegion = null;
    this.areaSearchText = '';
  }

}
